/* ═══════════════════════════════════════════════════════════
   js/components/project-picker.js — <project-picker> Web Component
   Реюзабельный пикер «ЖК/Проект»: мини-фото комплекса + название.
   Визуально повторяет выпадашку «Проект» жилого каталога (novostrojki
   .nv-proj-opt), но самодостаточен (Shadow DOM) — стили инкапсулированы,
   ноль зависимостей от страничного CSS, ноль коллизий классов.

   jk-redesign 6.1k: заменяет голый <select> «Все ЖК» на flats.php тем же
   красивым компонентом, что и «Проекты» на novostrojki. novostrojki
   остаётся на своей inline-реализации (регресс-защита) — этот компонент
   является DRY-примитивом, на который каталог сможет мигрировать позже.

   API (host-owned state — страница управляет URL/restore/reset):
     • el.setProjects([{ id, name, image_url, flat_count }])  — наполнить
     • el.value = '<id>' | ''                                 — выбрать (silent)
     • el.value                                               — текущий id
     • event 'project-change' { detail: { value, name } }     — ТОЛЬКО по клику
   Атрибуты:
     • data-placeholder — текст кнопки без выбора (default «Все ЖК»)
     • data-all-label   — подпись опции «сброс» (default «Все ЖК»)
   ═══════════════════════════════════════════════════════════ */
(() => {
  'use strict';
  if (customElements.get('project-picker')) return;

  class ProjectPicker extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this._projects = [];
      this._value = '';
      this._open = false;
      this._onDocClick = this._onDocClick.bind(this);
    }

    get value() { return this._value; }
    set value(v) { this._value = (v == null ? '' : String(v)); this._applySelection(); }

    get placeholder() { return this.getAttribute('data-placeholder') || 'Все ЖК'; }
    get allLabel()    { return this.getAttribute('data-all-label')   || 'Все ЖК'; }

    connectedCallback() {
      this._render();
      document.addEventListener('click', this._onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
    }

    // ─── helpers ───
    _esc(s) { const d = document.createElement('div'); d.textContent = (s == null ? '' : s); return d.innerHTML; }

    // Thumb через /img-thumb.php (паритет с novostrojki thumbUrl). Только http(s).
    _thumb(src, w) {
      if (!src || typeof src !== 'string') return '';
      if (!/^https?:\/\//i.test(src)) return src;
      return '/img-thumb.php?src=' + encodeURIComponent(src) + '&w=' + (w || 96);
    }

    // ─── public API ───
    setProjects(list) {
      this._projects = Array.isArray(list) ? list : [];
      this._renderList();
      this._applySelection();
    }

    // ─── render ───
    _render() {
      this.shadowRoot.innerHTML = `
        <style>
          :host { display: inline-block; position: relative; width: 100%; }
          * { box-sizing: border-box; }
          .btn {
            display: flex; align-items: center; gap: 8px; width: 100%;
            min-height: 44px; padding: 10px 16px; border-radius: 10px;
            font-size: 14px; font-weight: 600; line-height: 1.2;
            border: 1px solid var(--border, rgba(255,255,255,.08));
            color: var(--text-muted, #8a8a8a); background: var(--dark3, rgba(255,255,255,.04));
            cursor: pointer; transition: all .2s; text-align: left;
            font-family: var(--font-body, 'Mulish', sans-serif);
          }
          .btn__ico { font-size: 13px; color: var(--text-dim, #6a6a72); flex: none; }
          .btn__label { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .btn__arrow { font-size: 9px; color: var(--text-dim, #6a6a72); transition: transform .2s; flex: none; }
          .btn:hover { border-color: rgba(200,169,110,.3); color: var(--text, #e8e6e1); background: rgba(255,255,255,.06); }
          :host([data-open]) .btn { border-color: var(--gold, #c8a96e); color: var(--gold, #c8a96e); background: rgba(200,169,110,.08); }
          :host([data-open]) .btn__arrow { transform: rotate(180deg); }
          .btn:focus-visible { outline: 2px solid var(--gold, #c8a96e); outline-offset: 2px; }

          .panel {
            display: none; position: absolute; top: calc(100% + 6px); left: 0; right: 0;
            min-width: 248px; padding: 8px; z-index: 210;
            background: var(--dark2, rgba(24,24,28,.96)); backdrop-filter: blur(24px);
            border: 1px solid rgba(200,169,110,.15); border-radius: 12px;
            box-shadow: 0 12px 40px rgba(0,0,0,.5);
          }
          :host([data-open]) .panel { display: block; }
          .list { display: flex; flex-direction: column; gap: 2px; max-height: 340px; overflow-y: auto; }

          .opt {
            display: flex; align-items: center; gap: 11px; width: 100%;
            min-height: 44px; padding: 8px 10px; border: none; background: transparent;
            border-radius: 10px; cursor: pointer; text-align: left;
            transition: background .18s ease;
            font-family: var(--font-body, 'Mulish', sans-serif);
          }
          .opt:hover { background: rgba(200,169,110,.1); }
          .opt.active { background: rgba(200,169,110,.16); }
          .opt:focus-visible { outline: 2px solid var(--gold, #c8a96e); outline-offset: -2px; }
          .opt__img, .opt__ph {
            width: 44px; height: 44px; flex: none;
            border-radius: 9px; object-fit: cover; background: var(--dark3, #1a1a24);
          }
          .opt__ph {
            display: flex; align-items: center; justify-content: center;
            color: var(--gold, #c8a96e); font-size: 16px; background: rgba(200,169,110,.1);
          }
          .opt__name {
            flex: 1 1 auto; min-width: 0; font-size: 13.5px; font-weight: 600;
            color: var(--text, #e8e6e1);
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          }
          .opt.active .opt__name { color: var(--gold, #c8a96e); }
          .opt__cnt {
            flex: none; font-size: 12px; font-weight: 700; color: var(--text-dim, #6a6a72);
            background: rgba(255,255,255,.05); padding: 2px 8px; border-radius: 20px;
          }
          @media (max-width: 768px) {
            .panel { position: absolute; left: 0; right: 0; min-width: 0; }
            .list { max-height: 60vh; }
          }
        </style>
        <button class="btn" type="button" id="btn" aria-haspopup="listbox" aria-expanded="false">
          <i class="btn__ico fa-solid fa-city" aria-hidden="true"></i>
          <span class="btn__label" id="label"></span>
          <i class="btn__arrow fa-solid fa-chevron-down" aria-hidden="true"></i>
        </button>
        <div class="panel" role="listbox"><div class="list" id="list"></div></div>
      `;
      this.shadowRoot.getElementById('btn').addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggle();
      });
      this._renderList();
      this._applySelection();
    }

    _renderList() {
      const list = this.shadowRoot && this.shadowRoot.getElementById('list');
      if (!list) return;

      const sorted = this._projects.slice().sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), 'ru'));

      let html = '<button class="opt" type="button" data-project="" role="option">'
        + '<span class="opt__ph"><i class="fa-solid fa-border-all" aria-hidden="true"></i></span>'
        + '<span class="opt__name">' + this._esc(this.allLabel) + '</span></button>';

      sorted.forEach((it) => {
        const id = String(it.id == null ? '' : it.id);
        const photo = it.image_url || '';
        const thumb = photo
          ? '<img class="opt__img" src="' + this._esc(this._thumb(photo, 96)) + '" alt="" loading="lazy" decoding="async">'
          : '<span class="opt__ph"><i class="fa-solid fa-building" aria-hidden="true"></i></span>';
        const cnt = (it.flat_count != null && it.flat_count !== '')
          ? '<span class="opt__cnt">' + this._esc(String(it.flat_count)) + '</span>'
          : '';
        html += '<button class="opt" type="button" data-project="' + this._esc(id) + '" role="option">'
          + thumb
          + '<span class="opt__name">' + this._esc(it.name || 'ЖК') + '</span>'
          + cnt + '</button>';
      });

      list.innerHTML = html;
      list.querySelectorAll('[data-project]').forEach((opt) => {
        opt.addEventListener('click', (e) => {
          e.stopPropagation();
          this._select(opt.dataset.project || '', true);
        });
      });
    }

    // Программный выбор (silent) vs пользовательский клик (emit).
    _select(id, emit) {
      this._value = id == null ? '' : String(id);
      this._applySelection();
      this._setOpen(false);
      if (emit) {
        this.dispatchEvent(new CustomEvent('project-change', {
          bubbles: true, composed: true,
          detail: { value: this._value, name: this._selectedName() }
        }));
      }
    }

    _selectedName() {
      const hit = this._projects.find(p => String(p.id) === this._value);
      return hit ? String(hit.name || '') : '';
    }

    _applySelection() {
      if (!this.shadowRoot) return;
      const label = this.shadowRoot.getElementById('label');
      const name = this._selectedName();
      if (label) label.textContent = (this._value && name) ? name : this.placeholder;
      this.shadowRoot.querySelectorAll('[data-project]').forEach((opt) => {
        opt.classList.toggle('active', (opt.dataset.project || '') === this._value);
      });
    }

    // ─── open/close ───
    _toggle() { this._setOpen(!this._open); }
    _setOpen(on) {
      this._open = !!on;
      if (this._open) this.setAttribute('data-open', ''); else this.removeAttribute('data-open');
      const btn = this.shadowRoot.getElementById('btn');
      if (btn) btn.setAttribute('aria-expanded', this._open ? 'true' : 'false');
    }
    _onDocClick(e) {
      if (!this._open) return;
      if (e.composedPath && e.composedPath().includes(this)) return;
      this._setOpen(false);
    }
  }

  // Esc закрывает открытый пикер (глобально — Shadow DOM keydown всплывает как host).
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('project-picker[data-open]').forEach((el) => {
      if (typeof el._setOpen === 'function') el._setOpen(false);
    });
  });

  customElements.define('project-picker', ProjectPicker);
})();
